import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "technical-audits",
  kind: "horizontal",
  title: "Technical Audits",
  shortTitle: "Audits",
  status: "draft",
  tier: "we-do",
  seoTitle: "Technical Audits | General Applications",
  seoDescription:
    "Independent technical audits of software, data, operational workflows, security controls, and engineering delivery.",
  summary:
    "An independent assessment of the systems, workflows, controls, and technical decisions that need a clear account of what is true, what is at risk, and what should happen next.",
  image: "/images/blog/reconciling-fragmented-data-hero.webp",
  relatedCapabilitySlugs: ["software-architecture-engineering"],
  relatedIndustrySlugs: ["enterprise", "energy", "military"],
  relatedPostSlugs: [
    "legacy-system-modernization",
    "auditing-the-undocumented",
  ],
  engineering: [
    "Software architecture, codebase, dependency, and delivery-health assessment",
    "Data-model, source-of-truth, reconciliation, and pipeline review",
    "Operational workflow, exception-path, and handoff analysis",
    "Technical-control, configuration, and audit-evidence review",
    "Requirements, interface, vendor, and implementation-readiness evaluation",
    "Prioritized findings with decision context and practical next steps",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "An Audit Should Create Direction",
    },
    {
      type: "paragraph",
      text: "A useful technical audit is not a list of generic best practices. It starts with the actual artifacts: the system, the data, the operating workflow, the controls, the constraints, and the decisions people need to make. The result is a clear account of what is known, what needs verification, and where focused work is justified.",
    },
    {
      type: "heading",
      level: 2,
      text: "Findings Need Context",
    },
    {
      type: "paragraph",
      text: "The same observed issue can be a tolerable tradeoff, an operational risk, or a blocker depending on the work around it. Findings are therefore tied to ownership, evidence, dependencies, and a realistic next decision—not presented as detached scores or a prewritten modernization plan.",
    },
  ],
  phases: {
    study: {
      focus:
        "Define the decision the audit must support, the systems and evidence in scope, and the people who can explain how the work actually happens.",
      produces: [
        "Audit question, boundary, and evidence plan",
        "System, workflow, and stakeholder inventory",
        "Risk, constraint, and decision-context map",
        "Access and interview plan proportionate to the question",
      ],
      fit: {
        good: [
          "Teams that need an independent technical read before committing to a change, vendor, remediation, or roadmap",
          "Organizations with conflicting accounts of system health, data quality, operational risk, or implementation readiness",
        ],
        poor: [
          "A request for a certification or legal determination rather than a technical assessment",
          "A request for conclusions without access to the systems, artifacts, or people that establish the facts",
        ],
      },
    },
    "initial-design": {
      focus:
        "Build an assessment model that connects observed technical facts to the decisions, risks, and ownership they affect.",
      produces: [
        "Assessment framework tailored to the operating context",
        "Evidence, interview, and artifact-review plan",
        "Finding classification and decision-trace model",
        "Initial priority and dependency view",
      ],
    },
    "detailed-design": {
      focus:
        "Examine the system and its operating model in enough detail to distinguish assumptions, defects, constraints, and deliberate tradeoffs.",
      produces: [
        "Architecture, workflow, data, or control findings grounded in reviewed evidence",
        "Root-cause and dependency analysis for material findings",
        "Options with assumptions, tradeoffs, and ownership",
        "Reviewable audit report and decision materials",
      ],
    },
    implementation: {
      focus:
        "Work with the responsible team to validate findings, clarify the recommended sequence, and hand over material that can guide the next work.",
      produces: [
        "Validated findings and agreed decision record",
        "Prioritized remediation, modernization, or investigation backlog",
        "Risk and dependency handoff for implementation planning",
        "Follow-up review criteria for the chosen next step",
      ],
    },
  },
};
